import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CrudService } from '../crud.service';
import { Bookmark } from '../../models/bookmark';
import { BookmarkValidationResult } from '../../models/bookmark-validation';
import { BookmarkInventory } from '../../models/bookmark-inventory';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BookmarkService extends CrudService<Bookmark> {
  protected basePath = 'bookmark'; // Specific for BookmarkService
  public instanceType = 'bookmark'; // Used in base service for constructing URLs

  constructor(http: HttpClient) {
    super(http);
  }

  async getByCategoryIds(categoryIds: string[]) {
    const headers = new HttpHeaders({
      'X-Category-Ids': JSON.stringify(categoryIds),
    });
    return await lastValueFrom(
      this.http.get<{ [key: string]: Bookmark }>(
        `${this.getBaseUrl()}/categories`,
        { headers: headers }
      )
    );
  }
  /**
   * Retrieves a bookmark from the server based on the provided token.
   *
   * @param {string} token - The unique token of the item to retrieve.
   * @return {Promise<Bookmark>} A promise that resolves to the retrieved item of type T.
   */
  getByGuestToken(token: string) {
    return lastValueFrom(
      this.http.get<Bookmark>(`${this.getBaseUrl()}/token/${token}`)
    );
  }

  /**
   * Validates all views of a commune on the server (against the database).
   *
   * @param {string} communeId - The commune whose views are validated.
   * @return {Promise<BookmarkValidationResult[]>} One result per view.
   */
  validateByCommune(communeId: string) {
    return lastValueFrom(
      this.http.get<BookmarkValidationResult[]>(
        `${this.getBaseUrl()}/commune/${communeId}/validate`
      )
    );
  }

  /**
   * Everything a view uses (widgets, indicators, spaces & years, data,
   * references) with status, and what publishing it still needs.
   *
   * @param {string} bookmarkId - The view.
   * @return {Promise<BookmarkInventory>} The inventory.
   */
  getInventory(bookmarkId: string) {
    return lastValueFrom(
      this.http.get<BookmarkInventory>(
        `${this.getBaseUrl()}/${bookmarkId}/inventory`
      )
    );
  }

  /**
   * Validates one saved view on the server.
   *
   * @param {string} bookmarkId - The view to validate.
   * @return {Promise<BookmarkValidationResult>} The validation result.
   */
  validateOne(bookmarkId: string) {
    return lastValueFrom(
      this.http.get<BookmarkValidationResult>(
        `${this.getBaseUrl()}/${bookmarkId}/validate`
      )
    );
  }

  /**
   * Validates unsaved view data on the server, e.g. before publishing.
   *
   * @param {Partial<Bookmark>} bookmark - The view as it would be saved.
   * @return {Promise<BookmarkValidationResult>} The validation result.
   */
  validateDraft(bookmark: Partial<Bookmark>) {
    return lastValueFrom(
      this.http.post<BookmarkValidationResult>(
        `${this.getBaseUrl()}/validate`,
        bookmark
      )
    );
  }

  /**
   * Verifies a guest link password on the server.
   *
   * @param {string} token - The guest link token.
   * @param {string} password - The password entered by the guest.
   * @return {Promise<Bookmark>} The full view incl. its guest access token; rejects on a wrong password.
   */
  verifyGuestPassword(token: string, password: string) {
    return lastValueFrom(
      this.http.post<Bookmark>(`${this.getBaseUrl()}/token/${token}/verify`, {
        password,
      })
    );
  }
}
